import { API_TAGS } from '@/shared/api/api-tags';
import { baseApi } from '@/shared/api/base-api';
import { normalizePro20Response } from '@/shared/api/pro20-transform';
import type { PaginatedResponse } from '@/shared/types/api.types';
import type { Pro20ApiResponse } from '@/shared/types/api-response.types';

import type {
  ChangeCollaboratorPasswordPayload,
  Collaborator,
  CollaboratorCodeParams,
  CollaboratorDetailsParams,
  CollaboratorListParams,
  CreateCollaboratorParams,
  MobileDeviceLoginParams,
  SendCollaboratorCredentialsPayload,
  UpdateCollaboratorParams,
} from '../types/collaborator.types';
import type {
  BranchFcmTokens,
  CollaboratorPresenceReport,
  CollaboratorReportIdParams,
  CollaboratorReportSummary,
  CollaboratorReportsByDateParams,
  CollaboratorReportsRangeParams,
} from '../types/collaborator-report.types';
import {
  normalizeCollaborator,
  normalizeCollaboratorsList,
  normalizeCollaboratorsResponse,
} from '../utils/normalize-collaborators';
import {
  normalizeBranchFcmTokens,
  normalizePresenceReport,
  normalizePresenceReportsList,
  normalizeReportSummariesList,
} from '../utils/normalize-collaborator-reports';

function buildCollaboratorInvalidationTags(
  employeeId?: number,
  branchCode?: string,
) {
  const tags: { type: typeof API_TAGS.Collaborators; id: string }[] = [];

  if (employeeId) {
    tags.push({ type: API_TAGS.Collaborators, id: String(employeeId) });
  }

  if (branchCode) {
    tags.push({ type: API_TAGS.Collaborators, id: branchCode });
  }

  return tags;
}

export const collaboratorsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getCollaborators: builder.query<
      PaginatedResponse<Collaborator>,
      CollaboratorListParams
    >({
      query: ({
        branchCode,
        page = 0,
        size = 20,
        search,
        sortBy = 'lastName',
        sortDirection = 'asc',
      }) => ({
        url: `/report/getemployeebybranchcode/${branchCode}`,
        method: 'GET',
        params: {
          page,
          size,
          sortBy,
          sortDirection,
          ...(search?.trim() ? { search: search.trim() } : {}),
        },
      }),
      transformResponse: (
        response: Pro20ApiResponse<unknown>,
        _meta,
        arg,
      ) => {
        const results = normalizePro20Response(response);
        return normalizeCollaboratorsResponse(results, arg.size ?? 20, {
          page: arg.page ?? 0,
          size: arg.size ?? 20,
          search: arg.search,
        });
      },
      providesTags: (_result, _error, { branchCode }) => [
        { type: API_TAGS.Collaborators, id: branchCode },
      ],
    }),

    getCollaboratorDetails: builder.query<
      Collaborator,
      CollaboratorDetailsParams
    >({
      query: ({ employeeId }) => ({
        url: `/report/getemployee/${String(employeeId)}`,
        method: 'GET',
      }),
      transformResponse: (response: Pro20ApiResponse<unknown>) => {
        const data = normalizePro20Response(response);
        const normalized = normalizeCollaborator(data);
        if (!normalized) {
          throw new Error('Invalid collaborator details response');
        }
        return normalized;
      },
      providesTags: (_result, _error, { employeeId }) => [
        { type: API_TAGS.Collaborators, id: String(employeeId) },
      ],
    }),

    getAllCollaborators: builder.query<Collaborator[], undefined>({
      query: () => ({
        url: '/report/employee/getall',
        method: 'GET',
      }),
      transformResponse: (response: Pro20ApiResponse<unknown>) =>
        normalizeCollaboratorsList(normalizePro20Response(response)),
      providesTags: [{ type: API_TAGS.Collaborators, id: 'ALL' }],
    }),

    getFiredCollaborators: builder.query<Collaborator[], { branchCode: string }>(
      {
        query: ({ branchCode }) => ({
          url: `/report/getfiredemployeebybranchcode/${branchCode}`,
          method: 'GET',
        }),
        transformResponse: (response: Pro20ApiResponse<unknown>) =>
          normalizeCollaboratorsList(normalizePro20Response(response)),
        providesTags: (_result, _error, { branchCode }) => [
          { type: API_TAGS.Collaborators, id: `${branchCode}-fired` },
        ],
      },
    ),

    createCollaborator: builder.mutation<Collaborator, CreateCollaboratorParams>(
      {
        query: ({ branchCode, sendCredentials, body }) => ({
          url: `/report/employee/create/${branchCode}`,
          method: 'POST',
          params: { sendCredentials },
          body,
        }),
        transformResponse: (response: Pro20ApiResponse<unknown>) => {
          const normalized = normalizeCollaborator(
            normalizePro20Response(response),
          );
          if (!normalized) {
            throw new Error('Invalid create collaborator response');
          }
          return normalized;
        },
        invalidatesTags: (_result, _error, { branchCode }) => [
          { type: API_TAGS.Collaborators, id: branchCode },
        ],
      },
    ),

    updateCollaborator: builder.mutation<Collaborator, UpdateCollaboratorParams>(
      {
        query: ({ branchCode, body }) => ({
          url: `/report/employee/update/${branchCode}`,
          method: 'PUT',
          body,
        }),
        transformResponse: (response: Pro20ApiResponse<unknown>) => {
          const normalized = normalizeCollaborator(
            normalizePro20Response(response),
          );
          if (!normalized) {
            throw new Error('Invalid update collaborator response');
          }
          return normalized;
        },
        invalidatesTags: (result, _error, { branchCode }) =>
          buildCollaboratorInvalidationTags(result?.employeeId, branchCode),
      },
    ),

    changeCollaboratorPassword: builder.mutation<
      null,
      ChangeCollaboratorPasswordPayload
    >({
      query: (body) => ({
        url: '/employees/change-password',
        method: 'PUT',
        body,
      }),
      transformResponse: (response: unknown) => {
        if (response == null) {
          return null;
        }
        normalizePro20Response(response);
        return null;
      },
    }),

    sendCollaboratorCredentials: builder.mutation<
      Record<string, unknown> | null,
      SendCollaboratorCredentialsPayload
    >({
      query: ({ branchCode, number, message, smsFallbackMessage }) => ({
        url: `/communicationservice/api/wsapicontroller/sendmessage/${encodeURIComponent(branchCode)}`,
        method: 'POST',
        body: {
          number,
          message,
          sms_fallback_message: smsFallbackMessage,
        },
      }),
      transformResponse: (response: unknown) => {
        if (response && typeof response === 'object') {
          return response as Record<string, unknown>;
        }
        return null;
      },
    }),

    hideCollaborator: builder.mutation<null, CollaboratorCodeParams>({
      query: ({ employeeId }) => ({
        url: `/report/hideemployee/${String(employeeId)}`,
        method: 'PUT',
      }),
      transformResponse: () => null,
      invalidatesTags: (_result, _error, { employeeId, branchCode }) =>
        buildCollaboratorInvalidationTags(employeeId, branchCode),
    }),

    fireCollaborator: builder.mutation<null, CollaboratorCodeParams>({
      query: ({ employeeId }) => ({
        url: `/report/fireemployee/${String(employeeId)}`,
        method: 'PUT',
      }),
      transformResponse: () => null,
      invalidatesTags: (_result, _error, { employeeId, branchCode }) =>
        buildCollaboratorInvalidationTags(employeeId, branchCode),
    }),

    deleteCollaborator: builder.mutation<null, CollaboratorCodeParams>({
      query: ({ employeeId }) => ({
        url: `/report/deleteemployee/${String(employeeId)}`,
        method: 'DELETE',
      }),
      transformResponse: () => null,
      invalidatesTags: (_result, _error, { employeeId, branchCode }) =>
        buildCollaboratorInvalidationTags(employeeId, branchCode),
    }),

    loginCollaboratorMobileDevice: builder.mutation<
      Collaborator,
      MobileDeviceLoginParams
    >({
      query: ({ branchCode, username, password, body }) => ({
        url: `/report/employee/mobiledevice/login/${branchCode}`,
        method: 'POST',
        params: { username, password },
        body,
      }),
      transformResponse: (response: Pro20ApiResponse<unknown>) => {
        const normalized = normalizeCollaborator(
          normalizePro20Response(response),
        );
        if (!normalized) {
          throw new Error('Invalid mobile login response');
        }
        return normalized;
      },
    }),

    getCollaboratorReportSummaries: builder.query<
      CollaboratorReportSummary[],
      CollaboratorReportsRangeParams
    >({
      query: ({ branchCode, initialRangeDate, endRangeDate }) => ({
        url: `/report/retrievereports/${branchCode}`,
        method: 'GET',
        params: { initialRangeDate, endRangeDate },
      }),
      transformResponse: (response: Pro20ApiResponse<unknown>) =>
        normalizeReportSummariesList(normalizePro20Response(response)),
      providesTags: (_result, _error, { branchCode }) => [
        { type: API_TAGS.Collaborators, id: `${branchCode}-reports` },
      ],
    }),

    getCollaboratorReportsByDate: builder.query<
      CollaboratorPresenceReport[],
      CollaboratorReportsByDateParams
    >({
      query: ({ branchCode, zonedDateTime }) => ({
        url: `/report/retrievereport/${branchCode}`,
        method: 'GET',
        params: { zonedDateTime },
      }),
      transformResponse: (response: Pro20ApiResponse<unknown>) =>
        normalizePresenceReportsList(normalizePro20Response(response)),
      providesTags: (_result, _error, { branchCode }) => [
        { type: API_TAGS.Collaborators, id: `${branchCode}-reports-by-date` },
      ],
    }),

    getCollaboratorReportById: builder.query<
      CollaboratorPresenceReport,
      CollaboratorReportIdParams
    >({
      query: ({ id }) => ({
        url: `/report/getreport/${String(id)}`,
        method: 'GET',
      }),
      transformResponse: (response: Pro20ApiResponse<unknown>) => {
        const normalized = normalizePresenceReport(
          normalizePro20Response(response),
        );
        if (!normalized) {
          throw new Error('Invalid report response');
        }
        return normalized;
      },
      providesTags: (_result, _error, { id }) => [
        { type: API_TAGS.Collaborators, id: `report-${String(id)}` },
      ],
    }),

    createCollaboratorReports: builder.mutation<
      CollaboratorPresenceReport[],
      CollaboratorPresenceReport[]
    >({
      query: (body) => ({
        url: '/report/create/reports',
        method: 'POST',
        body,
      }),
      transformResponse: (response: Pro20ApiResponse<unknown>) =>
        normalizePresenceReportsList(normalizePro20Response(response)),
      invalidatesTags: [{ type: API_TAGS.Collaborators, id: 'LIST' }],
    }),

    deleteCollaboratorReport: builder.mutation<
      null,
      CollaboratorReportIdParams
    >({
      query: ({ id }) => ({
        url: `/report/report/${String(id)}`,
        method: 'DELETE',
      }),
      transformResponse: () => null,
      invalidatesTags: [{ type: API_TAGS.Collaborators, id: 'LIST' }],
    }),

    getBranchFcmTokens: builder.query<BranchFcmTokens, { branchCode: string }>({
      query: ({ branchCode }) => ({
        url: `/report/retrievefcmtokenlist/${branchCode}`,
        method: 'GET',
      }),
      transformResponse: (response: Pro20ApiResponse<unknown>) =>
        normalizeBranchFcmTokens(normalizePro20Response(response)),
    }),
  }),
});

export const {
  useGetCollaboratorsQuery,
  useGetCollaboratorDetailsQuery,
  useGetAllCollaboratorsQuery,
  useGetFiredCollaboratorsQuery,
  useCreateCollaboratorMutation,
  useUpdateCollaboratorMutation,
  useChangeCollaboratorPasswordMutation,
  useSendCollaboratorCredentialsMutation,
  useHideCollaboratorMutation,
  useFireCollaboratorMutation,
  useDeleteCollaboratorMutation,
  useLoginCollaboratorMobileDeviceMutation,
  useGetCollaboratorReportSummariesQuery,
  useGetCollaboratorReportsByDateQuery,
  useGetCollaboratorReportByIdQuery,
  useCreateCollaboratorReportsMutation,
  useDeleteCollaboratorReportMutation,
  useGetBranchFcmTokensQuery,
} = collaboratorsApi;
